import { useOrgDepartments } from "../../../../../../services/useOrgData";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, IconButton, InputBase, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import TopBar from "../../../../../../component/TopBar";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import SectionLoader from "../../../../../../component/SectionLoader";
import EmptyState from "../../../../../../component/EmptyState";
import { toneColors } from "../../../../../../component/statusTone";
import { FilterSelect, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getMuster } from "../../../../../../services/facialRecognition";

import { createBulkAttendanceRequests } from "../../../../../../services/attendanceRequest";
import BulkAttestDialog from "../../../../../../component/attendance/BulkAttestDialog";
import MusterPrintSheet from "../../../../../../component/attendance/MusterPrintSheet";
import { buildAttendanceMonth, daysInMonth, toDayString } from "../../../../../../utils/attendanceMonth";
import { buildMusterCsv, downloadCsv } from "../../../../../../utils/musterExport";
import { KIND, classifyDay, tallyRow, tallyColumns } from "../../../../../../utils/musterTally";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, radius } from "../../../../../../theme";

import { pressable } from "../../../../../../component/pressableProps";
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_INITIAL = ["S", "M", "T", "W", "T", "F", "S"];
const NAME_COL = "13rem";
const CELL = "1.75rem";
// wide enough for "23 / 25" without wrapping
const RATIO_COL = "3.75rem";
const TALLY_COL = "2rem";
// One screen of roster. The payload is a month of day records per employee, so
// the page size bounds the response, not just the rendering.
const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// Days a bulk correction can land on. Same rule as the single-day editor in the
// register: a correction needs a real working day with a resolvable shift, and
// leave, holidays and weekly offs are not punch problems.
const CORRECTABLE = new Set(["MISSING_PUNCH", "ABSENT", "PRESENT", "LATE"]);
// what a device failure actually produces — preselected when a column is picked
const PRESELECT = new Set(["MISSING_PUNCH", "ABSENT"]);

/**
 * Every symbol the grid can draw, named.
 *
 * Presence leads because it is the thing that is *not* marked: the reader needs
 * to be told that the faint dot is the ordinary case before the tinted letters
 * mean anything. The leave codes are grouped because the letters come from the
 * organisation's own leave types — CL and SL are the common two, but a company
 * with a "COMP" code will see COMP in the grid, and the legend must not imply
 * a fixed list it does not control.
 */
const LEGEND = [
  { sym: "·", label: "Present", tone: "none" },
  { sym: "A", label: "Absent", tone: "danger" },
  { sym: "MP", label: "Missing punch", tone: "violet" },
  { sym: "L / CL / SL", label: "Leave", tone: "info" },
  { sym: "H", label: "Holiday", tone: "muted" },
];

// The name column stays put while thirty-one day columns scroll past it; a row
// whose employee has scrolled off the left is unreadable.
const stickyNameSx = {
  width: NAME_COL,
  minWidth: NAME_COL,
  position: "sticky",
  left: 0,
  zIndex: 2,
  borderRight: `1px solid ${tokens.border}`,
  p: "0.5rem 0.75rem",
};

// The listing head style, at the density a 31-column grid can afford. Same
// weight, colour, tracking and case as every other table head in the product —
// only the vertical padding and the rule differ, because this header is two
// lines tall and the grid draws its own borders.
const headBaseSx = {
  ...headCellSx,
  borderBottom: "none",
  py: 0.75,
  display: "flex",
  alignItems: "flex-end",
};

/**
 * The whole roster for one month, one row per employee.
 *
 * Statuses come from buildAttendanceMonth — the same resolver the individual
 * register uses — run per employee over data the API returns as ingredients.
 * A second copy of the precedence rules on the server is how the two views
 * would drift apart, so there isn't one.
 */
const AttendanceMuster = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const today = useMemo(() => new Date(), []);

  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [search, setSearch] = useState("");
  // what has actually been sent to the server. Typing should not fire a request
  // per keystroke, and the roster is a month of day records per employee.
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  // Filtered server-side rather than in the browser: a supervisor who wants
  // their own crew should not be made to download 400 employees' day records
  // first. Search stays client-side — it is a within-page find, not a fetch.
  const [department, setDepartment] = useState("");
  const { data: departments } = useOrgDepartments();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [exporting, setExporting] = useState(false);
  const [printRows, setPrintRows] = useState(null);
  // the day a bulk correction is being staged against, and who is included
  const [attestDay, setAttestDay] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [dialogOpen, setDialogOpen] = useState(false);

  const requestId = useRef(0);
  const { year, month } = cursor;

  const fetchData = useCallback(async ({ quiet = false } = {}) => {
    const id = requestId.current + 1;
    requestId.current = id;
    if (!quiet) setLoading(true);
    setFailed(false);
    const res = await getMuster({
      year, month, department, q: query, limit: pageSize, skip: page * pageSize,
    });
    if (id !== requestId.current) return;
    if (res?.success === false) {
      setFailed(true);
      notify.error(res?.message || "Failed to load muster");
    }
    setData(res?.data || null);
    setLoading(false);
  }, [year, month, department, query, page, pageSize, notify]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const departmentOptions = useMemo(
    () => [
      { value: "", label: "All departments" },
      ...departments.map((d) => ({ value: d._id, label: d.name })),
    ],
    [departments]
  );

  const rows = useMemo(() => {
    if (!data?.employees) return [];
    // no client-side filter: the server searched, and re-filtering here would
    // narrow a page the server already narrowed
    return data.employees
      .map((e) => ({
        ...e,
        ...buildAttendanceMonth({
          year,
          month,
          records: e.records,
          shift: e.shift,
          holidays: data.holidays,
          leave: e.leave,
          today,
        }),
      }));
  }, [data, year, month, today]);

  const total = daysInMonth(year, month);
  const dayHeaders = useMemo(
    () =>
      Array.from({ length: total }, (_, i) => {
        const d = i + 1;
        const weekdayIndex = new Date(Date.UTC(year, month, d)).getUTCDay();
        const dayString = toDayString(year, month, d);
        const todayString = toDayString(today.getFullYear(), today.getMonth(), today.getDate());
        return {
          d,
          dayString,
          weekday: WEEKDAY_INITIAL[weekdayIndex],
          // the hairline falls on the first day of the week, so the month reads
          // as four or five blocks rather than thirty-one identical columns
          startsWeek: weekdayIndex === 0,
          isToday: dayString === todayString,
          isFuture: dayString > todayString,
        };
      }),
    [total, year, month, today]
  );

  // Who was present each day, across the rows on screen. Not the whole roster:
  // the figure has to agree with the column above it, and a total drawn from
  // pages the reader cannot see would not.
  const columnTally = useMemo(() => tallyColumns(rows, total), [rows, total]);

  /**
   * The week hairline and today's rule, as one box-shadow.
   *
   * Today wins over the week division when they land on the same column — the
   * marigold is the one thing on this screen that is allowed to shout, and two
   * rules on the same edge would only blur it.
   */
  const columnRule = (h) => {
    if (!h) return "";
    if (h.isToday) return `inset 1px 0 0 ${tokens.marigold}, inset -1px 0 0 ${tokens.marigold}`;
    if (h.startsWeek) return `inset 1px 0 0 ${tokens.borderStrong}`;
    return "";
  };

  const step = (delta) =>
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });

  const exitAttest = () => {
    setAttestDay(null);
    setPicked(new Set());
  };

  // Any change of roster or month invalidates a staged selection: the picked
  // ids would otherwise survive into a grid that no longer shows those rows,
  // and the next Apply would correct people the supervisor cannot see.
  useEffect(() => {
    setAttestDay(null);
    setPicked(new Set());
  }, [department, year, month, page, pageSize, query]);

  // a narrower roster may not have as many pages as the one being viewed
  useEffect(() => { setPage(0); }, [department, year, month, query, pageSize]);

  const rosterTotal = data?.meta?.total ?? rows.length;
  const pageCount = Math.max(1, Math.ceil(rosterTotal / pageSize));
  const firstShown = rosterTotal === 0 ? 0 : page * pageSize + 1;
  const lastShown = Math.min(rosterTotal, page * pageSize + (data?.employees?.length || 0));

  // Clicking a day header stages that whole column. A device failure is the case
  // this serves, so the days it actually produces — missing punches and
  // absences — start selected; the rest of the column stays available but off,
  // because "correct everyone" on a day most people worked normally is wrong.
  const startAttest = (dayString) => {
    if (attestDay === dayString) return exitAttest();
    const next = new Set();
    rows.forEach((r) => {
      const d = r.days.find((x) => x.dayString === dayString);
      if (d && PRESELECT.has(d.status.key)) next.add(String(r.id));
    });
    setAttestDay(dayString);
    setPicked(next);
  };

  const togglePicked = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // the crew as the dialog needs them, in the order the grid shows
  const attestSelection = useMemo(() => {
    if (!attestDay) return [];
    return rows
      .filter((r) => picked.has(String(r.id)))
      .map((r) => {
        const d = r.days.find((x) => x.dayString === attestDay);
        return { id: String(r.id), name: r.name, statusLabel: d ? d.status.label : "—" };
      });
  }, [rows, picked, attestDay]);

  // Seed the dialog from the shift the selected crew is actually on, but only
  // when they agree. Mixed shifts have no single right answer, so it asks rather
  // than guessing one crew's hours onto another's.
  const attestShift = useMemo(() => {
    if (!attestDay) return { start: null, end: null };
    const weekday = new Date(`${attestDay}T00:00:00Z`).getUTCDay();
    const seen = new Set();
    let start = null;
    let end = null;
    rows
      .filter((r) => picked.has(String(r.id)))
      .forEach((r) => {
        const s = r.shift?.days?.[weekday];
        const key = s ? `${s.startTime}-${s.endTime}` : "none";
        seen.add(key);
        if (s && start == null) {
          start = s.startTime ?? null;
          end = s.endTime ?? null;
        }
      });
    return seen.size === 1 ? { start, end } : { start: null, end: null };
  }, [rows, picked, attestDay]);

  const applyBulk = async (payload) => {
    const res = await createBulkAttendanceRequests({
      dayString: attestDay,
      source: "DEVICE_FAILURE",
      ...payload,
    });
    if (res?.success) await fetchData({ quiet: true });
    return res;
  };

  const closeDialog = (didApply) => {
    setDialogOpen(false);
    if (didApply) {
      notify.success("Corrections applied");
      exitAttest();
    }
  };

  // The sheet covers the whole filtered roster, not the page on screen.
  // Exporting only the visible page would hand someone a sign-off sheet that
  // silently omits most of their crew — the same hazard as an unlabelled
  // departmental sheet, and harder to notice.
  // Every page of the current filter, resolved. Both the sheet and the file are
  // the whole roster: one that quietly contained only the fifty rows on screen
  // would be signed as though it were complete.
  const gatherAll = async () => {
    const all = [];
    let holidays = data?.holidays || [];
    for (let skip = 0; skip < rosterTotal; skip += pageSize) {
      // reuse what is already loaded rather than refetching this page
      if (skip === page * pageSize && data?.employees) {
        all.push(...data.employees);
        continue;
      }
      const res = await getMuster({ year, month, department, q: query, limit: pageSize, skip });
      if (res?.success === false) throw new Error(res?.message || "Failed to load the full roster");
      all.push(...(res?.data?.employees || []));
      holidays = res?.data?.holidays || holidays;
    }
    return all
      .map((e) => ({
        ...e,
        ...buildAttendanceMonth({ year, month, records: e.records, shift: e.shift, holidays, leave: e.leave, today }),
      }));
  };

  const deptName = () => departments.find((d) => d._id === department)?.name || null;

  const exportSignOff = async () => {
    if (!rows.length || exporting) return;
    setExporting(true);
    try {
      const exportRows = await gatherAll();
      const csv = buildMusterCsv({
        rows: exportRows,
        year,
        monthName: MONTHS[month],
        totalDays: daysInMonth(year, month),
        scopeLabel: deptName(),
      });
      const slug = deptName() ? `-${deptName().toLowerCase().replace(/[^a-z0-9]+/g, "-")}` : "";
      downloadCsv(`muster-${year}-${String(month + 1).padStart(2, "0")}${slug}.csv`, csv);
    } catch (err) {
      notify.error(err?.message || "Couldn't build the sign-off sheet");
    } finally {
      setExporting(false);
    }
  };

  // A sheet to sign on paper. The browser's own print is enough — a generated
  // PDF would add a dependency to produce the same page, and this way the
  // printer dialog's own controls (orientation, scale) stay available.
  const printSignOff = async () => {
    if (!rows.length || exporting) return;
    setExporting(true);
    try {
      setPrintRows(await gatherAll());
      // let the print-only markup mount before the dialog opens over it
      setTimeout(() => window.print(), 50);
    } catch (err) {
      notify.error(err?.message || "Couldn't build the sign-off sheet");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar
        MainHeading={"Attendance"}
        breadcrumbs={[{ label: "Attendance", href: "/app/attendance-dashboard" }, { label: "Muster" }]}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton aria-label="Previous" size="small" onClick={() => step(-1)} sx={{ border: `1px solid ${tokens.border}` }}>
            <ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
          <Box sx={{ fontSize: "1rem", fontWeight: 600, minWidth: "9rem", textAlign: "center" }}>
            {MONTHS[month]} {year}
          </Box>
          <IconButton aria-label="Next" size="small" onClick={() => step(1)} sx={{ border: `1px solid ${tokens.border}` }}>
            <ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: "wrap" }}>
          {/* Position and paging live under the grid instead, beside the count
              they describe — up here they competed with the month for the
              reader's first glance, and the month is what the screen is about. */}
          <FilterSelect
            inputProps={{ "aria-label": "Department" }}
            value={department}
            options={departmentOptions}
            onChange={(e) => setDepartment(e.target.value)}
          />
          <BtnSecondary
            onClick={exportSignOff}
            disabled={!rows.length || exporting}
            sx={{ height: "2.5rem", px: "1rem", borderRadius: "10px", fontSize: "0.875rem" }}
          >
            {exporting ? "Preparing…" : "Export CSV"}
          </BtnSecondary>
          <BtnSecondary
            onClick={printSignOff}
            disabled={!rows.length || exporting}
            sx={{ height: "2.5rem", px: "1rem", borderRadius: "10px", fontSize: "0.875rem" }}
          >
            Print sign-off sheet
          </BtnSecondary>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: "10px",
              px: 1.5,
              height: "2.5rem",
              minWidth: "14rem",
              background: tokens.card,
            }}
          >
            <SearchIcon sx={{ fontSize: "1.125rem", color: tokens.textMuted }} />
            <InputBase
              placeholder="Search employee"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ fontSize: "0.875rem", flex: 1 }}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Stays put while the column is staged: the supervisor scrolls the grid
          to check who is included, and an action bar that scrolled away with it
          would make them scroll back to act. */}
      {attestDay && !loading && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            mb: 1.5,
            p: "0.75rem 1rem",
            borderRadius: "10px",
            border: `1px solid ${tokens.accent}59`,
            background: `${tokens.accentTint}`,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {new Date(`${attestDay}T00:00:00Z`).getUTCDate()} {MONTHS[month]}
            </Box>{" "}
            — {picked.size} of {rows.length} selected. Click a cell to include or exclude it.
          </Box>
          <Stack direction="row" spacing={1}>
            <BtnSecondary onClick={exitAttest} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
              Cancel
            </BtnSecondary>
            <BtnPrimary
              onClick={() => setDialogOpen(true)}
              disabled={picked.size === 0}
              sx={{ height: "2.25rem", px: "1.25rem", borderRadius: "8px", fontSize: "0.8125rem" }}
            >
              Correct {picked.size}
            </BtnPrimary>
          </Stack>
        </Stack>
      )}

      {loading ? (
        <SectionLoader minHeight="60vh" />
      ) : failed ? (
        <EmptyState variant="error" title="Couldn't load muster" onRetry={fetchData} />
      ) : rows.length === 0 ? (
        <EmptyState
          title={query ? "No matching employees" : "No employees"}
          description={query ? "Nothing matches that search." : "There is nobody to show for this month."}
        />
      ) : (
        <>
          <Box
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: `${radius.card}px`,
              background: tokens.card,
              overflow: "hidden",
            }}
          >
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: "max-content" }}>
                {/* header: the weekday letter above the date, so a Monday can be
                    found by its shape rather than by counting cells */}
                <Stack
                  direction="row"
                  sx={{ borderBottom: `1px solid ${tokens.border}`, background: tokens.surface }}
                >
                  <Box sx={{ ...stickyNameSx, background: tokens.surface, ...headBaseSx, alignItems: "flex-end" }}>
                    Employee
                  </Box>
                  {dayHeaders.map((h) => {
                    const staged = attestDay === h.dayString;
                    const rule = columnRule(h);
                    return (
                      <Tooltip key={h.d} arrow title={staged ? "Cancel selection" : "Select this day to correct"}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => startAttest(h.dayString)}
                          sx={{
                            width: CELL,
                            minWidth: CELL,
                            py: 0.5,
                            px: 0,
                            font: "inherit",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            background: staged ? tokens.accentTint : "transparent",
                            boxShadow: [rule, staged ? `inset 0 -2px 0 ${tokens.accent}` : ""]
                              .filter(Boolean)
                              .join(", ") || "none",
                            "&:hover": { background: staged ? `${tokens.accent}26` : tokens.surfaceSubtle },
                            "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "-2px" },
                          }}
                        >
                          <Box sx={{ fontSize: "0.625rem", lineHeight: 1.4, color: tokens.textPlaceholder }}>
                            {h.weekday}
                          </Box>
                          <Box
                            sx={{
                              fontSize: "0.6875rem",
                              lineHeight: 1.4,
                              fontVariantNumeric: "tabular-nums",
                              fontWeight: h.isToday ? 700 : 500,
                              color: h.isToday
                                ? tokens.text
                                : h.startsWeek || h.isFuture
                                ? tokens.textPlaceholder
                                : tokens.textMuted,
                            }}
                          >
                            {h.d}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {/* Present carries its denominator, so the three figures are
                      one reading rather than a number and two footnotes. */}
                  <Box sx={{ ...headBaseSx, width: RATIO_COL, minWidth: RATIO_COL, justifyContent: "center", alignItems: "flex-end" }}>
                    Present
                  </Box>
                  <Box sx={{ ...headBaseSx, width: TALLY_COL, minWidth: TALLY_COL, justifyContent: "center", alignItems: "flex-end" }}>
                    A
                  </Box>
                  <Box sx={{ ...headBaseSx, width: TALLY_COL, minWidth: TALLY_COL, justifyContent: "center", alignItems: "flex-end" }}>
                    L
                  </Box>
                </Stack>

                {rows.map((r) => {
                  const t = tallyRow(r.days);
                  return (
                    <Stack
                      key={String(r.id)}
                      direction="row"
                      sx={{
                        borderBottom: `1px solid ${tokens.border}`,
                        "&:hover .muster-name": { background: tokens.surfaceSubtle },
                      }}
                    >
                      <Box
                        className="muster-name"
                        {...pressable(() => navigate(`/app/employee-details/${r.id}/attendance`))}
                        sx={{
                          ...stickyNameSx,
                          zIndex: 1,
                          background: tokens.card,
                          display: "block",
                          cursor: "pointer",
                          minHeight: CELL,
                          py: "0.4rem",
                        }}
                      >
                        <Box
                          sx={{
                            ...bodyCellSx,
                            borderBottom: "none",
                            py: 0,
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.name}
                        </Box>
                        <Box
                          sx={{
                            fontSize: "0.6875rem",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                            color: tokens.textMuted,
                          }}
                        >
                          {r.aliasId || "—"}
                          {!r.shift && " · no shift"}
                        </Box>
                      </Box>

                      {r.days.map((d, i) => {
                        const h = dayHeaders[i];
                        const cell = classifyDay(d);
                        const tinted = cell.tone !== "none";
                        // Presence is the default, so it is a dot with no fill
                        // rather than a tinted block. It is green rather than
                        // grey: the dot still has to be legible on the sheet
                        // someone signs, and a 1.9:1 grey was not — the book was
                        // revised to the success ink for exactly that reason.
                        // A corrected one takes the plum so the mark is visible
                        // without becoming an exception in its own right.
                        const fg = tinted
                          ? toneColors(cell.tone).fg
                          : cell.corrected
                          ? tokens.primary
                          : tokens.msgSuccessText;
                        const bg = tinted ? toneColors(cell.tone).bg : "transparent";
                        const inColumn = attestDay === d.dayString;
                        const selectable = inColumn && CORRECTABLE.has(d.status.key);
                        const isPicked = selectable && picked.has(String(r.id));
                        return (
                          <Tooltip
                            key={d.dayString}
                            arrow
                            title={`${r.name} · ${d.weekday} ${d.dayNum} — ${d.remark || d.status.label}${
                              d.corrected ? " · punch corrected" : ""
                            }${
                              cell.kind === KIND.MISSING_PUNCH ? " · not counted as present until regularised" : ""
                            }${selectable ? (isPicked ? " · included" : " · click to include") : ""}`}
                          >
                            <Box
                              component={selectable ? "button" : "div"}
                              type={selectable ? "button" : undefined}
                              {...pressable(selectable ? () => togglePicked(String(r.id)) : undefined)}
                              aria-pressed={selectable ? isPicked : undefined}
                              sx={{
                                width: CELL,
                                minWidth: CELL,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                py: "0.2rem",
                                font: "inherit",
                                border: "none",
                                cursor: selectable ? "pointer" : "default",
                                boxShadow: columnRule(h) || "none",
                                // the staged column stays visible down the whole
                                // grid, so it is obvious which day is being acted on
                                background: inColumn ? tokens.accentTint : "transparent",
                              }}
                            >
                              <Box
                                sx={{
                                  width: "1.5rem",
                                  height: "1.5rem",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "5px",
                                  fontSize: cell.symbol.length > 1 ? "0.625rem" : "0.8125rem",
                                  fontWeight: tinted ? 700 : 600,
                                  letterSpacing: cell.corrected ? "0.02em" : 0,
                                  color: fg,
                                  background: bg,
                                  boxShadow: isPicked
                                    ? `0 0 0 2px ${tokens.card}, 0 0 0 3px ${tokens.accent}`
                                    : "none",
                                  opacity: inColumn && !selectable ? 0.45 : 1,
                                }}
                              >
                                {/* the asterisk exists so whoever signs the sheet
                                    knows which days were edited rather than captured */}
                                {cell.symbol}
                                {cell.corrected ? "*" : ""}
                              </Box>
                            </Box>
                          </Tooltip>
                        );
                      })}

                      {/* 23 / 25 — a bare 23 means nothing to someone checking a
                          month with a holiday in it. */}
                      <Tooltip
                        arrow
                        title={`${t.present} present of ${t.workingDays} days rostered · ${t.absent} absent · ${t.leave} on leave${
                          t.missingPunch
                            ? ` · ${t.missingPunch} missing punch${t.missingPunch === 1 ? "" : "es"} not counted as present`
                            : ""
                        }`}
                      >
                        <Box
                          sx={{
                            width: RATIO_COL,
                            minWidth: RATIO_COL,
                            alignSelf: "center",
                            textAlign: "right",
                            pr: "0.35rem",
                            fontSize: "0.75rem",
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box component="span" sx={{ fontWeight: 700, color: tokens.text }}>
                            {t.present}
                          </Box>
                          <Box component="span" sx={{ color: tokens.textPlaceholder }}> / {t.workingDays}</Box>
                        </Box>
                      </Tooltip>
                      {[
                        { v: t.absent, tone: "danger" },
                        { v: t.leave, tone: "info" },
                      ].map((s, i) => (
                        <Box
                          key={i}
                          sx={{
                            width: TALLY_COL,
                            minWidth: TALLY_COL,
                            textAlign: "center",
                            alignSelf: "center",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                            color: s.v ? toneColors(s.tone).fg : tokens.textPlaceholder,
                          }}
                        >
                          {s.v}
                        </Box>
                      ))}
                    </Stack>
                  );
                })}

                {/* Who was on the floor each day. It reddens on a day the team
                    was below full strength — that is the data telling the truth
                    about a thin day, not the page reporting a failure. */}
                <Stack direction="row" sx={{ background: tokens.surface, borderTop: `1px solid ${tokens.border}` }}>
                  <Box sx={{ ...stickyNameSx, background: tokens.surface, ...headBaseSx, alignItems: "center" }}>
                    Present that day
                  </Box>
                  {columnTally.map((c, i) => {
                    const h = dayHeaders[i];
                    const none = c.expected === 0;
                    return (
                      <Tooltip
                        key={h.dayString}
                        arrow
                        title={
                          none
                            ? `${h.weekday} ${h.d} — nobody rostered`
                            : `${h.weekday} ${h.d} — ${c.present} of ${c.expected} present`
                        }
                      >
                        <Box
                          sx={{
                            width: CELL,
                            minWidth: CELL,
                            height: "1.75rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.6875rem",
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: c.short ? 700 : 400,
                            color: none ? tokens.textPlaceholder : c.short ? tokens.msgDangerText : tokens.textMuted,
                            boxShadow: columnRule(h) || "none",
                          }}
                        >
                          {none ? "—" : c.present}
                        </Box>
                      </Tooltip>
                    );
                  })}
                  <Box sx={{ width: RATIO_COL, minWidth: RATIO_COL }} />
                  <Box sx={{ width: TALLY_COL, minWidth: TALLY_COL }} />
                  <Box sx={{ width: TALLY_COL, minWidth: TALLY_COL }} />
                </Stack>
              </Box>
            </Box>
          </Box>

          {/* Every symbol on the grid, named. The muster is read by people who
              open it twice a month, and a legend is cheaper than a convention
              they have to remember. */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{ mt: 1.5, gap: 2, flexWrap: "wrap", fontSize: "0.75rem", color: tokens.textMuted }}
          >
            {LEGEND.map((l) => {
              const tinted = l.tone !== "none";
              return (
                <Stack key={l.label} direction="row" alignItems="center" spacing={0.75}>
                  <Box
                    sx={{
                      minWidth: "1.625rem",
                      height: "1.25rem",
                      px: 0.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "4px",
                      fontSize: l.sym.length > 1 ? "0.625rem" : "0.8125rem",
                      fontWeight: 700,
                      color: tinted ? toneColors(l.tone).fg : tokens.msgSuccessText,
                      background: tinted ? toneColors(l.tone).bg : tokens.surface,
                    }}
                  >
                    {l.sym}
                  </Box>
                  {l.label}
                </Stack>
              );
            })}
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box component="span" sx={{ color: tokens.primary, fontWeight: 700 }}>·*</Box>
              Punch corrected
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Box>Blank column · weekly off</Box>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            sx={{
              mt: 2,
              pt: 2,
              gap: 2,
              flexWrap: "wrap",
              borderTop: `1px solid ${tokens.border}`,
            }}
          >
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
              {query
                ? `${rosterTotal} matching “${query}”`
                : `Showing ${firstShown}–${lastShown} of ${rosterTotal} ${rosterTotal === 1 ? "person" : "people"}`}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box component="span" sx={{ fontSize: "0.875rem", color: tokens.textMuted }}>
                Rows
              </Box>
              <FilterSelect
                inputProps={{ "aria-label": "Rows per page" }}
                value={pageSize}
                options={PAGE_SIZES.map((n) => ({ value: n, label: String(n) }))}
                onChange={(e) => setPageSize(Number(e.target.value))}
              />
            </Stack>
            {pageCount > 1 && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  sx={{ border: `1px solid ${tokens.border}` }}
                  aria-label="Previous page"
                >
                  <ChevronLeftIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
                <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, minWidth: "4.5rem", textAlign: "center" }}>
                  Page {page + 1} of {pageCount}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                  disabled={page >= pageCount - 1}
                  sx={{ border: `1px solid ${tokens.border}` }}
                  aria-label="Next page"
                >
                  <ChevronRightIcon sx={{ fontSize: "1rem" }} />
                </IconButton>
              </Stack>
            )}
          </Stack>
        </>
      )}

      {/* print-only: hidden on screen, and the only thing visible on paper */}
      {printRows && (
        <MusterPrintSheet
          rows={printRows}
          year={year}
          month={month}
          monthName={MONTHS[month]}
          scopeLabel={deptName()}
        />
      )}

      <BulkAttestDialog
        open={dialogOpen}
        dateLabel={
          attestDay
            ? `${new Date(`${attestDay}T00:00:00Z`).getUTCDate()} ${MONTHS[month]} ${year}`
            : ""
        }
        employees={attestSelection}
        shiftStart={attestShift.start}
        shiftEnd={attestShift.end}
        onClose={closeDialog}
        onSubmit={applyBulk}
      />
    </Box>
  );
};

export default AttendanceMuster;
